export interface KeyCapability {
  title: string;
  desc: string;
  iconName: string;
}

export interface Benefit {
  title: string;
  metric: string;
  desc: string;
}

export interface FAQ {
  q: string;
  a: string;
}

export interface ServiceDetail {
  id: string;
  title: string;
  tagline: string;
  description: string;
  overview: string;
  iconName: string;
  capabilities: KeyCapability[];
  solutions: string[];
  benefits: Benefit[];
  industries: string[];
  techStack: string[];
  whyVivify: { title: string; desc: string }[];
  faqs: FAQ[];
  relatedLinks?: { label: string; path: string }[];
  heroCta?: string;
  seoTitle?: string;
  metaDesc?: string;
}

export const servicesData: ServiceDetail[] = [
  {
    id: 'ai-powered-solutions',
    title: 'AI-Powered Solutions for Smarter Business Operations',
    tagline: 'Vivify builds AI-powered solutions including automation systems, predictive analytics, intelligent dashboards, AI chatbots, and business intelligence platforms.',
    description: 'AI-powered solutions are software systems that use artificial intelligence to automate tasks, analyze data, generate insights, support decisions, and improve business efficiency. Our solutions reduce manual work, improve reporting and enable better decisions.',
    overview: 'Vivify’s AI capabilities include automation, analytics, chatbots, predictive systems, reporting dashboards and intelligent business applications that integrate directly into your core operations.',
    iconName: 'Brain',
    heroCta: 'Talk to AI Expert',
    seoTitle: 'AI-Powered Solutions | Automation, Analytics & AI Systems',
    metaDesc: 'Vivify builds AI-powered solutions including automation systems, predictive analytics, intelligent dashboards, AI chatbots, and business intelligence platforms.',
    capabilities: [
      {
        title: 'AI Automation Solutions',
        desc: 'Autonomous multi-step reasoning agents that parse emails, verify claims, and coordinate inventory cycles without human intervention.',
        iconName: 'Zap'
      },
      {
        title: 'Predictive Systems',
        desc: 'Advanced statistical engines utilizing custom regression and time-series models to predict market trends and demand peaks.',
        iconName: 'TrendingUp'
      },
      {
        title: 'AI Chatbots',
        desc: 'Private, domain-specific chat interfaces trained on proprietary corporate wikis and databases for customer support and internal workflows.',
        iconName: 'MessageSquare'
      },
      {
        title: 'Intelligent Analytics',
        desc: 'Advanced data analytics pipelines that uncover hidden patterns and generate actionable business insights from raw data.',
        iconName: 'Activity'
      },
      {
        title: 'AI Model Development',
        desc: 'Custom neural architectures and domain-specific fine-tuning for specialized business terminology and sensor logs.',
        iconName: 'Cpu'
      },
      {
        title: 'AI Business Assistants',
        desc: 'Custom co-pilots integrated into workspace tools to assist employees with automated boilerplate drafts and lead generation.',
        iconName: 'Users'
      },
      {
        title: 'AI Reporting Dashboards',
        desc: 'Intelligent dashboards providing real-time data visualization, automated anomaly flags, and comprehensive analytics reporting.',
        iconName: 'Layout'
      },
      {
        title: 'AI Use Cases',
        desc: 'Practical business use cases including automating repetitive tasks, improving reporting, predicting risks, and analyzing business data.',
        iconName: 'Briefcase'
      }
    ],
    solutions: [
      'Enterprise Software Solutions',
      'Custom Product Development',
      'Cloud & IT Services',
      'Related Products'
    ],
    benefits: [
      {
        title: 'Decision Velocity',
        metric: 'Faster',
        desc: 'Cognitive agents process and format inbound queries instantly, clearing triage queues and improving reporting.'
      },
      {
        title: 'Prediction Accuracy',
        metric: 'Precise',
        desc: 'Deep learning models predict risks and minimize false alarms on anomaly checks.'
      },
      {
        title: 'Efficiency Gains',
        metric: 'High',
        desc: 'Saves administrative hours weekly by automating manual workflow tasks.'
      },
      {
        title: 'Operational Decisions',
        metric: 'Smarter',
        desc: 'Generates insights to support better decisions and improve overall business efficiency.'
      }
    ],
    industries: ['Manufacturing', 'Healthcare', 'Logistics', 'Airports', 'Real Estate', 'Retail', 'Enterprise Organizations'],
    techStack: ['PyTorch', 'TensorFlow', 'LangChain', 'Hugging Face', 'Python', 'FastAPI', 'Kubernetes'],
    whyVivify: [
      {
        title: 'AI Automation -> Intelligent Automation Systems',
        desc: 'Visit our Solutions page to see how AI automation reduces manual work and streamlines business processes.'
      },
      {
        title: 'Predictive Analytics -> AI Business Solutions',
        desc: 'Explore the Solutions page to learn more about our predictive analytics and business intelligence platforms.'
      },
      {
        title: 'AI Chatbots -> Custom Product Development',
        desc: 'Check out Custom Product Development to see how we build AI chatbots for customer support and lead generation.'
      },
      {
        title: 'AI Dashboards -> Enterprise Software Solutions',
        desc: 'Visit Enterprise Software Solutions to learn about intelligent dashboards and AI reporting.'
      }
    ],
    faqs: [
      {
        q: 'What are AI-powered solutions?',
        a: 'AI-powered solutions are software systems that use artificial intelligence to automate tasks, analyze data, generate insights, support decisions, and improve business efficiency.'
      },
      {
        q: 'How can AI help my business?',
        a: 'AI can help your business automate repetitive tasks, improve reporting, predict risks, support customers through chatbots, analyze business data, and improve operational decisions.'
      },
      {
        q: 'Does Vivify build AI chatbots?',
        a: 'Yes. Vivify develops AI chatbots for customer support, internal workflows, lead generation, employee assistance, and business process automation.'
      },
      {
        q: 'Can AI be integrated with existing software?',
        a: 'Yes. Vivify can integrate AI features into existing ERP, CRM, HRMS, workflow systems, dashboards, mobile apps, and cloud-based platforms.'
      },
      {
        q: 'Which Vivify products use AI?',
        a: 'Vivify’s AI product ecosystem includes Communai, Smilai, Factorai, and Securai, which use AI for operations, reporting, automation, safety, and industry intelligence.'
      }
    ],
    relatedLinks: [
      { label: 'Explore Solutions', path: '/solutions' },
      { label: 'View Products', path: '/products' },
      { label: 'View Case Studies', path: '/case-studies' },
      { label: 'Contact Vivify', path: '/contact' },
      { label: 'Services', path: '/services' }
    ]
  },
  {
    id: 'enterprise-software-solutions',
    title: '💼 Enterprise Software Solutions',
    tagline: 'Scalable enterprise software solutions including ERP, CRM, HRMS, workflow management, inventory systems, billing platforms, and business dashboards.',
    description: 'Streamline business operations with secure, scalable ERP, CRM, HRMS, and custom enterprise software designed to improve productivity and business performance.',
    overview: 'Enterprise software is a business application designed to manage, automate, and centralize operations across departments such as sales, HR, finance, inventory, production, and customer management. Vivify develops custom enterprise systems that eliminate manual processes, reduce repeated data entry, and provide comprehensive business control through role-based access, automated workflows, and integrated dashboards.',
    iconName: 'Building2',
    heroCta: 'Discuss Requirement',
    seoTitle: 'Enterprise Software Solutions | ERP, CRM, HRMS & Workflow Systems',
    metaDesc: 'Vivify develops scalable enterprise software solutions including ERP, CRM, HRMS, workflow management, inventory systems, billing platforms, and business dashboards.',
    capabilities: [
      {
        title: 'ERP Solutions',
        desc: 'Custom Enterprise Resource Planning systems that unify all business operations including finance, inventory, production, sales, and reporting into a single integrated platform.',
        iconName: 'Database'
      },
      {
        title: 'CRM Solutions',
        desc: 'Customer Relationship Management platforms that manage leads, track customer interactions, automate sales processes, and improve customer retention through comprehensive data management.',
        iconName: 'Users'
      },
      {
        title: 'HRMS Solutions',
        desc: 'Human Resource Management Systems for employee records, attendance tracking, leave management, payroll support, onboarding processes, and HR workflow automation.',
        iconName: 'ClipboardList'
      },
      {
        title: 'Workflow Management Systems',
        desc: 'Automated workflow engines with approval processes, task assignments, notifications, progress tracking, and department coordination for streamlined operations.',
        iconName: 'Workflow'
      },
      {
        title: 'Inventory Management Systems',
        desc: 'Real-time inventory tracking, stock management, purchase orders, supplier coordination, automated reorder processes, and warehouse management solutions.',
        iconName: 'Layers'
      },
      {
        title: 'Finance and Billing Systems',
        desc: 'Comprehensive financial management including automated invoicing, payment processing, expense tracking, financial reporting, tax compliance, and accounting integration.',
        iconName: 'FileText'
      },
      {
        title: 'Asset Management Systems',
        desc: 'Complete asset lifecycle management including tracking, maintenance scheduling, depreciation calculations, compliance monitoring, and asset optimization.',
        iconName: 'ShieldAlert'
      },
      {
        title: 'Purchase and Vendor Management',
        desc: 'Streamlined procurement processes, vendor relationship management, purchase approvals, contract management, and supplier performance tracking systems.',
        iconName: 'Briefcase'
      }
    ],
    solutions: [
      'Manual Excel-based tracking causing data inconsistencies and errors',
      'Disconnected departments requiring manual coordination and communication',
      'Delayed approval processes slowing down business operations',
      'Poor inventory visibility leading to stockouts or excess inventory',
      'Lack of real-time reports hindering management decisions',
      'Repeated data entry across multiple disconnected systems',
      'Limited management control and visibility over operations'
    ],
    benefits: [
      {
        title: 'Role-based Access',
        metric: 'Secure',
        desc: 'Comprehensive role-based access control ensuring users only access relevant information, maintaining security and operational efficiency across departments.'
      },
      {
        title: 'Approval Workflows',
        metric: 'Automated',
        desc: 'Streamlined approval processes with automatic routing, notifications, escalation rules, and complete audit trails for accountability and compliance.'
      },
      {
        title: 'Dashboards and Reports',
        metric: 'Real-time',
        desc: 'Interactive business dashboards and comprehensive reports providing real-time insights, KPI tracking, and data visualization for informed decision making.'
      },
      {
        title: 'Department Integration',
        metric: 'Unified',
        desc: 'Seamless integration between departments while maintaining modular architecture, eliminating data silos and improving cross-functional collaboration.'
      },
      {
        title: 'Notifications and Alerts',
        metric: 'Proactive',
        desc: 'Intelligent notification systems keeping teams informed about critical updates, deadlines, exceptions, and required actions across all business processes.'
      },
      {
        title: 'Mobile Integration',
        metric: 'Accessible',
        desc: 'Enterprise systems with web dashboards, mobile apps, cloud access, role-based permissions, and integrations with third-party platforms for complete accessibility.'
      }
    ],
    industries: ['Manufacturing', 'Healthcare', 'Logistics', 'Retail', 'Finance', 'Real Estate', 'Enterprise Organizations', 'Airports'],
    techStack: ['Node.js', 'React', 'PostgreSQL', 'GraphQL', 'Redis', 'Docker', 'Kubernetes', 'TypeScript', 'MongoDB'],
    whyVivify: [
      {
        title: 'Custom Business-Focused Development',
        desc: 'Vivify builds custom ERP systems based on your specific business workflows, departments, approval processes, reporting needs, and operational requirements rather than forcing generic solutions.'
      },
      {
        title: 'Comprehensive HRMS Capabilities',
        desc: 'We develop complete HRMS systems for employee records, attendance, leave management, payroll support, onboarding, asset tracking, and HR workflows tailored to your organization.'
      },
      {
        title: 'Seamless Integration Architecture',
        desc: 'Our enterprise solutions integrate with existing systems, mobile apps, cloud platforms, and third-party tools while maintaining role-based permissions and security protocols.'
      },
      {
        title: 'Scalable Modular Design',
        desc: 'Built with modular architecture that grows with your business - start with core modules and add ERP, CRM, HRMS, or inventory features as your operations expand.'
      }
    ],
    faqs: [
      {
        q: 'What is enterprise software?',
        a: 'Enterprise software is a business application designed to manage, automate, and centralize operations across departments such as sales, HR, finance, inventory, production, and customer management.'
      },
      {
        q: 'Does Vivify build custom ERP software?',
        a: 'Yes. Vivify builds custom ERP systems based on business workflows, departments, approvals, reporting needs, inventory processes, and operational requirements specific to your organization.'
      },
      {
        q: 'Can Vivify develop HRMS software?',
        a: 'Yes. Vivify can develop HRMS systems for employee records, attendance, leave, payroll support, onboarding, asset tracking, and HR workflows tailored to your business needs.'
      },
      {
        q: 'What business problems can enterprise software solve?',
        a: 'Enterprise software can reduce manual work, eliminate repeated data entry, improve approvals, centralize data, increase visibility, and provide real-time reports for management decision making.'
      },
      {
        q: 'Can enterprise software be integrated with mobile apps?',
        a: 'Yes. Vivify can build enterprise systems with web dashboards, mobile apps, cloud access, role-based permissions, and integrations with third-party platforms for complete accessibility.'
      }
    ],
    relatedLinks: [
      { label: 'Explore Solutions', path: '/solutions' },
      { label: 'View Products', path: '/products' },
      { label: 'View Case Studies', path: '/case-studies' },
      { label: 'Custom Product Development', path: '/services/custom-product-development' },
      { label: 'Cloud & IT Services', path: '/services/cloud-it-services' },
      { label: 'AI-Powered Solutions', path: '/services/ai-powered-solutions' },
      { label: 'Contact Vivify', path: '/contact' }
    ]
  },
  {
    id: 'industrial-software-automation',
    title: '🏭 Industrial Software & Automation',
    tagline: 'Cyber-physical sync, smart telemetry nodes, and live machine metrics.',
    description: 'Empower your manufacturing operations with intelligent industrial software, Industrial IoT, and smart factory solutions. Monitor production in real time, automate critical processes, and improve operational efficiency.',
    overview: 'Industrial facilities require high-performance, resilient software that survives tough environments. Vivify Soft Tech implements cyber-physical interfaces that collect sensor logs, evaluate assembly bottlenecks, and track overall equipment effectiveness (OEE) with millisecond precision.',
    iconName: 'Factory',
    capabilities: [
      {
        title: 'Industrial IoT Integration',
        desc: 'Connect PLC controllers, vibration sensors, and telemetry monitors directly to active edge database modules.',
        iconName: 'Cpu'
      },
      {
        title: 'Smart Factory Dashboards',
        desc: 'Live telemetry floor plans visualizing active, idle, or warning states across all assembly systems.',
        iconName: 'Layout'
      },
      {
        title: 'OEE Log Analysis',
        desc: 'Automated calculations showing availability, quality, and performance indexes for individual stations.',
        iconName: 'TrendingUp'
      },
      {
        title: 'Predictive Alert Tickets',
        desc: 'Trigger preventive service orders when vibration, temperature, or piece count patterns signal upcoming hardware errors.',
        iconName: 'ShieldAlert'
      },
      {
        title: 'Barcode & Production Checks',
        desc: 'Integrated scanners and light curtains ensuring assembly steps are finished correctly before items proceed.',
        iconName: 'CheckCircle'
      }
    ],
    solutions: [
      'Smart Factory Management Suite',
      'Real-Time Machine Telemetry Logs',
      'OEE Calculation and Bottleneck Tracking',
      'Predictive Maintenance Work Ticket Systems'
    ],
    benefits: [
      {
        title: 'Unplanned Downtime',
        metric: '-40% Downtime',
        desc: 'Early warning indicators detect hardware anomalies before major system breaks happen.'
      },
      {
        title: 'OEE Optimization',
        metric: '+18% Efficiency',
        desc: 'Identifies production line imbalances and bottlenecks to optimize factory layout.'
      },
      {
        title: 'Inventory Flow',
        metric: '99.6% Accuracy',
        desc: 'Raw materials are automatically tracked as they pass through factory stations.'
      },
      {
        title: 'Safety Metrics',
        metric: 'Zero Violations',
        desc: 'Ensures safety sequences and parameters are fully locked before heavy systems activate.'
      }
    ],
    industries: ['Manufacturing', 'Logistics', 'Airports', 'Warehousing', 'Utilities'],
    techStack: ['C++', 'Rust', 'MQTT', 'InfluxDB', 'Grafana', 'Docker Edge', 'Node-RED'],
    whyVivify: [
      {
        title: 'Protocol Agnostic Platforms',
        desc: 'We support all major industrial protocols including Modbus, OPC UA, BACnet, and Profinet.'
      },
      {
        title: 'Edge-First Resiliency',
        desc: 'Our software operates fully offline on factory floor edge servers, syncing data back to the cloud when connections are stable.'
      },
      {
        title: 'High-Frequency Processing',
        desc: 'Handles thousands of data points per second with lightweight memory footprints using Rust and C++ integrations.'
      },
      {
        title: 'Custom SCADA Replacements',
        desc: 'Replace slow, outdated SCADA screens with modern, interactive web systems accessible on tablets and web browsers.'
      }
    ],
    faqs: [
      {
        q: 'Do we need to buy new machinery to use your software?',
        a: 'No. We configure custom IIoT adapters that plug into existing PLC controllers or add low-cost external sensors to legacy systems.'
      },
      {
        q: 'How does the offline mode work during connection drops?',
        a: 'Edge gateways buffer sensor logs locally in SQLite or InfluxDB databases. Once internet connection returns, data is securely uploaded.'
      },
      {
        q: 'Can the system trigger emergency system cuts?',
        a: 'Yes, if authorized. We write low-level control code that communicates with PLCs to safely pause processes when safety bounds are crossed.'
      },
      {
        q: 'Is our data vulnerable to cyberattacks?',
        a: 'We isolate network traffic using corporate firewalls, disable public IP routes for edge servers, and encrypt all local storage databases.'
      },
      {
        q: 'How long does physical onsite setup take?',
        a: 'Depending on size, we conduct virtual mapping audits followed by 5 to 10 days of onsite calibration and network tuning.'
      }
    ]
  },
  {
    id: 'custom-product-development',
    title: '🚀 Custom Product Development',
    tagline: 'SaaS architectures, custom MVPs, and rapid prototype loops.',
    description: 'Turn your ideas into high-performing digital products with custom web, mobile, SaaS, and enterprise applications. We build scalable, user-centric solutions that accelerate innovation and business growth.',
    overview: 'Off-the-shelf software often fails to support unique business concepts. Vivify Soft Tech builds bespoke applications, cloud SaaS architectures, and high-frequency backend databases tailored to help businesses launch fast and scale without technical limits.',
    iconName: 'Globe',
    capabilities: [
      {
        title: 'SaaS Core Infrastructure',
        desc: 'Build secure multi-tenant architectures, subscription payment gateways, and admin control panels.',
        iconName: 'Layers'
      },
      {
        title: 'Rapid MVP Development',
        desc: 'Speed up launch schedules with functional, beautiful software versions optimized for user feedback.',
        iconName: 'Rocket'
      },
      {
        title: 'High-Performance API Sets',
        desc: 'Implement fast, standard REST, GraphQL, or gRPC endpoints to connect backend models to frontend widgets.',
        iconName: 'Cpu'
      },
      {
        title: 'Custom Business Platforms',
        desc: 'Scale secure internal web dashboards designed for heavy user traffic and database query loads.',
        iconName: 'Layout'
      },
      {
        title: 'Mobile App Building',
        desc: 'Responsive mobile systems built on React Native or Flutter to publish apps on iOS and Android app stores.',
        iconName: 'Smartphone'
      }
    ],
    solutions: [
      'Multi-Tenant SaaS Architectures',
      'Rapid MVP Launch Packages',
      'Corporate Web Dashboard Systems',
      'REST & gRPC Microservice APIs'
    ],
    benefits: [
      {
        title: 'Time to Market',
        metric: 'MVP in 8 Weeks',
        desc: 'Highly optimized development templates and sprints speed up launch coordinates.'
      },
      {
        title: 'Infrastructure Cost',
        metric: '-35% Cloud Fees',
        desc: 'Cloud-native serverless systems reduce database costs during quiet periods.'
      },
      {
        title: 'Development Scale',
        metric: 'Clean Codebase',
        desc: 'Fully modular, well-documented codebases make it easy for future devs to scale the product.'
      },
      {
        title: 'User Experience',
        metric: 'Premium UI/UX',
        desc: 'High-fidelity animations and responsive interfaces increase active user engagement.'
      }
    ],
    industries: ['Real Estate', 'Retail', 'Logistics', 'Healthcare', 'Enterprise Organizations'],
    techStack: ['React', 'TypeScript', 'Next.js', 'PostgreSQL', 'Go', 'AWS Lambda', 'Stripe'],
    whyVivify: [
      {
        title: 'Full Intellectual Ownership',
        desc: 'You own 100% of the code, IP rights, database layouts, and design artifacts. There are no vendor lock-ins.'
      },
      {
        title: 'Elite UI/UX Team',
        desc: 'We place visual quality and responsive usability at the center of every project we deploy.'
      },
      {
        title: 'Robust Scale from Day One',
        desc: 'We structure databases and caching layers to easily handle thousands of concurrent queries without crashes.'
      },
      {
        title: 'Agile Collaborative Sprints',
        desc: 'Access live demo setups weekly, paired with direct Slack/Teams feedback routes with our engineering leads.'
      }
    ],
    faqs: [
      {
        q: 'Do you help write specifications if we only have a basic concept?',
        a: 'Yes. We run discovery workshops to map out user stories, API structures, database schemas, and interface mockups.'
      },
      {
        q: 'Can we hire you to build the MVP and then take over the code?',
        a: 'Absolutely. We deliver fully documented codebases, Git history access, and support smooth transition handovers.'
      },
      {
        q: 'How do you test for bugs before deployment?',
        a: 'We implement automated unit checks, integration runs, API loading tests, and thorough manual QA evaluations.'
      },
      {
        q: 'What payment platforms do you support?',
        a: 'We integrate with Stripe, PayPal, Adyen, and regional gateways, including automated subscription and invoicing systems.'
      },
      {
        q: 'How do you handle future system upgrades?',
        a: 'We organize code into separate, modular components, making it simple to add or swap parts without breaking the main app.'
      }
    ]
  },
  {
    id: 'cloud-it-services',
    title: '☁️ Cloud & IT Services',
    tagline: 'Zero-trust networks, automated CI/CD pipelines, and multi-cloud systems.',
    description: 'Modernize your IT infrastructure with secure cloud solutions, managed services, and DevOps. Improve performance, strengthen security, and build a reliable foundation for business growth.',
    overview: 'Slow deployments and cloud billing surprises drag down modern businesses. Vivify Soft Tech designs automated multi-cloud infrastructures, secure zero-trust network controls, and Kubernetes orchestration systems to guarantee maximum uptime and reduce monthly costs.',
    iconName: 'Cloud',
    capabilities: [
      {
        title: 'Secure Cloud Migration',
        desc: 'Safe migration of active databases and workflows from onsite systems to AWS, Azure, or Google Cloud.',
        iconName: 'Database'
      },
      {
        title: 'CI/CD Pipeline Automation',
        desc: 'Automated release systems running testing checks and deploying updates in seconds with zero downtime.',
        iconName: 'RefreshCw'
      },
      {
        title: 'Zero-Trust Networks',
        desc: 'Secure corporate assets using multi-factor verification, isolated subnet limits, and continuous threat monitoring.',
        iconName: 'ShieldAlert'
      },
      {
        title: 'Kubernetes Orchestration',
        desc: 'Container management scaling backend services automatically based on live network demand.',
        iconName: 'Layers'
      },
      {
        title: 'Cloud Cost Optimization',
        desc: 'Continuous auditing of resource allocation, database sizing, and server routing to reduce unnecessary cloud fees.',
        iconName: 'TrendingUp'
      }
    ],
    solutions: [
      'Multi-Cloud AWS/Azure Infrastructure Design',
      'Automated Jenkins/GitHub Action Pipelines',
      'Zero-Trust Corporate VPN & Network Guardrails',
      '24/7 Cloud Performance Monitoring & Optimization'
    ],
    benefits: [
      {
        title: 'Server Uptime',
        metric: '99.99% Uptime',
        desc: 'Multi-region failover and load balancing prevent outages during heavy traffic.'
      },
      {
        title: 'Cloud Expenditure',
        metric: '-40% Cloud Cost',
        desc: 'Removing idle instances and autoscaling databases reduces monthly cloud fees.'
      },
      {
        title: 'Release Speed',
        metric: 'Deploy in Seconds',
        desc: 'CI/CD automation builds, tests, and deploys updates instantly.'
      },
      {
        title: 'Security Auditing',
        metric: 'SOC2 Ready',
        desc: 'Encrypted storage and active intrusion trackers meet modern compliance rules.'
      }
    ],
    industries: ['Airports', 'Logistics', 'Finance', 'Manufacturing', 'Enterprise Organizations'],
    techStack: ['AWS', 'Azure', 'Terraform', 'Docker', 'Kubernetes', 'GitHub Actions', 'Prometheus'],
    whyVivify: [
      {
        title: 'Infrastructure as Code (IaC)',
        desc: 'We write entire server environments in Terraform, making systems easily repeatable and simple to rebuild in minutes.'
      },
      {
        title: 'Certified Cloud Architects',
        desc: 'Our senior engineers hold certified AWS and Azure credentials and have experience managing large corporate operations.'
      },
      {
        title: 'Cost-Reduction Guarantee',
        desc: 'We audit your cloud environment and guarantee billing improvements in our initial design layouts.'
      },
      {
        title: 'Proactive System Defense',
        desc: 'We set up active intrusion alerts and automated firewalls to stop cyberthreats before they reach your database.'
      }
    ],
    faqs: [
      {
        q: 'Do you help migrate legacy physical servers to the cloud?',
        a: 'Yes. We map network connections, bundle databases safely, and perform migrations during quiet hours to minimize disruptions.'
      },
      {
        q: 'How do you handle multi-cloud management?',
        a: 'We use Terraform to manage configurations across multiple cloud providers (like AWS and Azure) from a single control interface.'
      },
      {
        q: 'What happens if a cloud provider has an outage?',
        a: 'We design systems with multi-region replication and automated DNS failover to switch traffic to healthy servers instantly.'
      },
      {
        q: 'Do you manage our systems month-to-month?',
        a: 'Yes, we offer ongoing managed cloud support, including system monitoring, regular upgrades, and security updates.'
      },
      {
        q: 'How do you track server errors and traffic peaks?',
        a: 'We set up real-time tools like Prometheus and Grafana, providing alerts for high CPU, memory, or network limits.'
      }
    ]
  }
];
